import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../types/order.type';
import { ServiceEvent } from '../../../services/services.service';
import { ordersService, servicesService } from '../../../services';

export interface ServiceCreatingScreenProps {
  order: Order;
  serviceId?: string;
  serviceType?: string;
  events?: ServiceEvent[];
  onServiceReady?: (serviceId: string) => void;
  onCancel?: () => void;
  onBack?: () => void;
  renderOrderDetails?: (order: Order) => React.ReactNode;
  maxPollingAttempts?: number;
  pollingInterval?: number;
}

const ServiceCreatingScreen: React.FC<ServiceCreatingScreenProps> = ({
  order,
  serviceId: initialServiceId,
  serviceType,
  events = [],
  onServiceReady,
  onCancel,
  onBack,
  renderOrderDetails,
  maxPollingAttempts = 4,
  pollingInterval = 30000, // 30 segundos
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serviceId, setServiceId] = useState<string | undefined>(initialServiceId || order.serviceId);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Función para obtener la ruta del servicio según el tipo
  const getServiceRoute = (type: string | undefined, serviceId: string): string | null => {
    if (!type || !serviceId) return null;
    
    const normalizedType = type.toLowerCase();
    
    if (normalizedType === 'app_deploy' || normalizedType === 'deploy-app') {
      return `/deploy-apps/${serviceId}`;
    }
    if (normalizedType === 'virtual-machine' || normalizedType === 'vm' || normalizedType === 'compute') {
      return `/virtual-machines/${serviceId}`;
    }
    if (normalizedType === 'database') {
      return `/databases/${serviceId}`;
    }
    if (normalizedType === 'site') {
      return `/sites/${serviceId}`;
    }
    if (normalizedType === 'app-server' || normalizedType === 'blueprint') {
      return `/stacks/${serviceId}`;
    }
    
    return null;
  };

  // Función para obtener el nombre del servicio según el tipo
  const getServiceTypeName = (type: string | undefined): string => {
    if (!type) return 'servicio';
    
    const normalizedType = type.toLowerCase();
    
    if (normalizedType === 'app_deploy' || normalizedType === 'deploy-app') {
      return 'aplicación';
    }
    if (normalizedType === 'virtual-machine' || normalizedType === 'vm' || normalizedType === 'compute') {
      return 'máquina virtual';
    }
    if (normalizedType === 'database') {
      return 'base de datos';
    }
    if (normalizedType === 'site') {
      return 'sitio';
    }
    if (normalizedType === 'app-server' || normalizedType === 'blueprint') {
      return 'stack';
    }
    
    return 'servicio';
  };

  // Función para obtener los mensajes según el tipo de servicio
  const getServiceMessages = () => {
    const currentServiceType = serviceType || order.metadata?.type;
    const serviceName = getServiceTypeName(currentServiceType);
    
    return {
      creating: `Tu ${serviceName} se está creando. Esto puede tardar unos minutos.`,
      creatingTitle: `Creando ${serviceName}...`,
      creatingDescription: `El ${serviceName} se está creando. Esto puede tardar unos minutos.`,
      notAvailable: `${serviceName.charAt(0).toUpperCase() + serviceName.slice(1)} aún no disponible`,
      notAvailableDescription: `El ${serviceName} aún no está listo después de varios intentos. Por favor, inténtalo más tarde.`
    };
  };

  // Función para verificar si el servicio existe
  const checkServiceExists = async (serviceId: string): Promise<boolean> => {
    try {
      await servicesService.getById(serviceId);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Función para iniciar el polling del servicio
  const startServicePolling = (serviceId: string) => {
    // Limpiar cualquier polling anterior
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    let attempts = 0;
    setMaxAttemptsReached(false);
    setIsPolling(true);

    // Verificar inmediatamente (intento 1)
    attempts = 1;
    setPollingAttempts(attempts);
    
    checkServiceExists(serviceId).then(exists => {
      if (exists) {
        const route = getServiceRoute(serviceType || order.metadata?.type, serviceId);
        if (route) {
          setIsPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setPollingAttempts(0);
          if (onServiceReady) {
            onServiceReady(serviceId);
          } else {
            navigate(route);
          }
        }
      } else {
        // Si ya alcanzamos el máximo de intentos, detener
        if (attempts >= maxPollingAttempts) {
          setMaxAttemptsReached(true);
          setIsPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          return;
        }

        // Iniciar polling cada X segundos
        const interval = setInterval(async () => {
          attempts++;
          setPollingAttempts(attempts);
          
          if (attempts >= maxPollingAttempts) {
            setMaxAttemptsReached(true);
            setIsPolling(false);
            clearInterval(interval);
            pollingRef.current = null;
            return;
          }
          
          const exists = await checkServiceExists(serviceId);
          if (exists) {
            const route = getServiceRoute(serviceType || order.metadata?.type, serviceId);
            if (route) {
              setIsPolling(false);
              clearInterval(interval);
              pollingRef.current = null;
              setPollingAttempts(0);
              if (onServiceReady) {
                onServiceReady(serviceId);
              } else {
                navigate(route);
              }
            }
          }
        }, pollingInterval);
        pollingRef.current = interval;
      }
    });
  };

  // Polling para verificar cuando se cree el servicio si no hay serviceId
  useEffect(() => {
    if (serviceId) {
      startServicePolling(serviceId);
    } else {
      // Si no hay serviceId, hacer polling de la orden
      let attempts = 0;
      
      const checkOrderForService = async () => {
        if (attempts >= maxPollingAttempts) {
          setMaxAttemptsReached(true);
          setIsPolling(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          return;
        }
        
        attempts++;
        setPollingAttempts(attempts);
        
        try {
          const updatedOrder = await ordersService.getById(order.id);
          if (updatedOrder.serviceId) {
            setServiceId(updatedOrder.serviceId);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            startServicePolling(updatedOrder.serviceId);
          }
        } catch (error) {
          console.error('Error checking order:', error);
        }
      };

      // Verificar inmediatamente
      checkOrderForService();
      
      // Polling cada X segundos
      const interval = setInterval(checkOrderForService, pollingInterval);
      pollingRef.current = interval;
    }

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [order.id, serviceId]);

  const handleCancel = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsPolling(false);
    if (onCancel) {
      onCancel();
    } else if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleRetry = () => {
    setPollingAttempts(0);
    setMaxAttemptsReached(false);
    setIsPolling(true);
    if (serviceId) {
      startServicePolling(serviceId);
    } else {
      // Reiniciar polling de la orden
      const checkOrderForService = async () => {
        try {
          const updatedOrder = await ordersService.getById(order.id);
          if (updatedOrder.serviceId) {
            setServiceId(updatedOrder.serviceId);
            startServicePolling(updatedOrder.serviceId);
          }
        } catch (error) {
          console.error('Error checking order:', error);
        }
      };
      checkOrderForService();
      const interval = setInterval(checkOrderForService, pollingInterval);
      pollingRef.current = interval;
    }
  };

  // Renderizar detalles de la orden personalizados o por defecto
  const renderDefaultOrderDetails = () => {
    const metadata = order.metadata || {};
    const config = metadata.vmConfig || metadata.appConfig || metadata.databaseConfig || metadata.siteConfig || metadata.stackConfig || {};

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('orders.detail.orderId')}</label>
          <code className="text-sm font-mono text-gray-900 dark:text-white">{order.id}</code>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('orders.detail.status')}</label>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            order.status === 'PAID' 
              ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' 
              : order.status === 'PENDING'
              ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
          }`}>
            {order.status === 'PAID' ? `✓ ${t('orders.detail.paid')}` : order.status === 'PENDING' ? `⏳ ${t('orders.detail.pending')}` : order.status}
          </span>
        </div>
        {config.name && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Nombre</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{config.name}</p>
          </div>
        )}
        {config.instanceType && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Tipo de Instancia</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{config.instanceType}</p>
          </div>
        )}
        {config.storage && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Almacenamiento</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{config.storage} GB</p>
          </div>
        )}
        {metadata.region && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Región</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{metadata.region}</p>
          </div>
        )}
        {order.createdAt && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('orders.detail.createdAt')}</label>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date(order.createdAt).toLocaleString()}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Obtener icono según el tipo de evento
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined className="text-green-600 dark:text-green-400" />;
      case 'error':
        return <CloseCircleOutlined className="text-red-600 dark:text-red-400" />;
      case 'warning':
        return <ClockCircleOutlined className="text-yellow-600 dark:text-yellow-400" />;
      default:
        return <InfoCircleOutlined className="text-blue-600 dark:text-blue-400" />;
    }
  };

  // Obtener estilos según el tipo de evento
  const getEventStyles = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack || (() => navigate(-1))}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeftOutlined />
            <span className="text-sm font-medium">{t('common.back')}</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header con animación */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-12 text-center">
            <div className="inline-block mb-4">
              {!maxAttemptsReached ? (
                <LoadingOutlined className="text-6xl text-white" spin />
              ) : (
                <ClockCircleOutlined className="text-6xl text-white" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {!maxAttemptsReached 
                ? getServiceMessages().creatingTitle
                : getServiceMessages().notAvailable}
            </h1>
            <p className="text-blue-100 text-lg">
              {!maxAttemptsReached
                ? getServiceMessages().creating
                : getServiceMessages().notAvailableDescription}
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Información de la orden */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('orders.detail.title')}</h2>
              {renderOrderDetails ? renderOrderDetails(order) : renderDefaultOrderDetails()}
            </div>

            {/* Progreso */}
            {!maxAttemptsReached && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {getServiceMessages().creatingTitle}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Intento {pollingAttempts} de {maxPollingAttempts}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 rounded-full animate-pulse"
                    style={{ width: `${(pollingAttempts / maxPollingAttempts) * 100}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                  <LoadingOutlined spin className="mr-2" />
                  Verificando cada 30 segundos...
                </p>
              </div>
            )}

            {/* Mensaje informativo */}
            <div className={`mt-8 border-l-4 p-4 rounded-r-lg ${
              maxAttemptsReached
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 dark:border-yellow-500'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-500'
            }`}>
              <div className="flex items-start gap-3">
                <InfoCircleOutlined className={`text-xl mt-0.5 flex-shrink-0 ${
                  maxAttemptsReached
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`} />
                <div>
                  <p className={`text-sm font-semibold mb-1 ${
                    maxAttemptsReached
                      ? 'text-yellow-900 dark:text-yellow-300'
                      : 'text-blue-900 dark:text-blue-300'
                  }`}>
                    {!maxAttemptsReached
                      ? getServiceMessages().creatingTitle
                      : getServiceMessages().notAvailable}
                  </p>
                  <p className={`text-sm ${
                    maxAttemptsReached
                      ? 'text-yellow-800 dark:text-yellow-200'
                      : 'text-blue-800 dark:text-blue-200'
                  }`}>
                    {!maxAttemptsReached
                      ? getServiceMessages().creatingDescription
                      : getServiceMessages().notAvailableDescription}
                  </p>
                </div>
              </div>
            </div>

            {/* Eventos si están disponibles */}
            {events.length > 0 && (
              <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('virtualMachines.detail.events.title') || 'Eventos'}
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className={`border rounded-lg p-4 ${getEventStyles(event.type)}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 mt-0.5">
                          {getEventIcon(event.type)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{event.message}</p>
                            <span className="text-xs flex-shrink-0 text-gray-500 dark:text-gray-400">
                              {event.timestamp.toLocaleString()}
                            </span>
                          </div>
                          {event.details && (
                            <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">{event.details}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div className="mt-8 flex gap-3 justify-end">
              <button
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {maxAttemptsReached ? t('common.back') : t('common.cancel') || 'Cancelar'}
              </button>
              {maxAttemptsReached && (
                <button
                  onClick={handleRetry}
                  className="px-6 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                >
                  {t('common.retry') || 'Reintentar'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCreatingScreen;

